import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavigateFunction, NavigateOptions, To } from 'react-router-dom';

/**
 * Wraps useNavigate to always preserve the current URL search string on every route
 * transition, so Zitadel ?token, ?stageId, ?gameSessionId, ?key are never stripped
 * when the app navigates between /, /game, /result, etc.
 *
 * Mirrors the behaviour of block-fill's hash-based router where query params live
 * before the # and are never touched by route changes.
 */
export function useAppNavigate(): NavigateFunction {
  const navigate = useNavigate();

  return useCallback((to: To | number, options?: NavigateOptions) => {
    if (typeof to === 'number') {
      navigate(to);
      return;
    }
    const search = globalThis.window.location.search;
    if (typeof to === 'string') {
      navigate({ pathname: to, search }, options);
    } else {
      // Partial<Path> object — only inject search if the caller didn't provide one
      navigate({ ...to, search: to.search ?? search }, options);
    }
  }, [navigate]) as NavigateFunction;
}
